export type ResolveQueryIdInput = {
  /** offset changed, everything else identical (engine's isPaginationOnly fast path). */
  isPaginationOnly: boolean;
  /** dataQuery object identity changed OR presetJson differs (preset activation/deactivation/reset). */
  didPresetChange: boolean;
  /** comparison (smartSearch+filters+sortBy) JSON differs from snapshot. */
  isFilterModified: boolean;
  /** queryModifier static filters are all present in the existing data-query. */
  isAllStaticFilterPresent: boolean;
  /** the active preset's dataQueryId (fallback). */
  presetDataQueryId: string;
  /** queryId from the URL search params, if any. */
  urlQueryId: string | undefined;
  /** queryId recorded in the engine's last fetch snapshot. */
  snapshotQueryId: string | null;
};

export type QueryIdResolution =
  | {kind: 'reuse'; queryId: string; skipCount: boolean}
  | {kind: 'create'};

/**
 * Pure classifier for the per-fetch queryId decision. Mirrors the branch order
 * previously inlined in GridDataEngine.fetchPageInternal:
 *   1. pagination only            → reuse URL/preset queryId, skip /count
 *   2. preset changed + static ok → reuse preset dataQueryId, with /count
 *   3. filters unchanged + static ok → reuse URL/preset queryId, skip /count iff snapshot matches
 *   4. otherwise                  → POST /createDataQuery
 */
export function resolveQueryId(input: ResolveQueryIdInput): QueryIdResolution {
  const reuseId = input.urlQueryId ?? input.presetDataQueryId;

  if (input.isPaginationOnly) {
    return {kind: 'reuse', queryId: reuseId, skipCount: true};
  }
  if (input.didPresetChange && input.isAllStaticFilterPresent) {
    return {kind: 'reuse', queryId: input.presetDataQueryId, skipCount: false};
  }
  if (!input.isFilterModified && input.isAllStaticFilterPresent) {
    return {kind: 'reuse', queryId: reuseId, skipCount: input.snapshotQueryId === reuseId};
  }
  return {kind: 'create'};
}
